import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Avatar,
  Typography,
  Chip,
  Grid,
  LinearProgress,
  Button,
  Badge,
  Divider,
  Stack,
} from '@mui/material';
import {
  Mail,
  TrendingUp,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '@/types';
import { formatPercent } from '@/shared/utils/formatPercentage';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  department: string;
  avatar: string;
  isActive: boolean;
  performanceRating: number;
  utilizationPercent: number;
  skills: string[];
  timezone: string;
}

interface TeamMemberTrackingCardProps {
  member: TeamMember;
  tasksCount?: number;
  projectsCount?: number;
  completedTasksToday?: number;
  dueTasksCount?: number;
}

const getRoleColor = (role: UserRole) => {
  switch (role) {
    case UserRole.SUPER_ADMIN:
      return 'error';
    case UserRole.ADMIN:
      return 'error';
    case UserRole.DEPARTMENT_HEAD:
      return 'primary';
    case UserRole.PROJECT_MANAGER:
      return 'info';
    case UserRole.TEAM_LEAD:
      return 'warning';
    case UserRole.MANAGER:
      return 'primary';
    default:
      return 'secondary';
  }
};

const getRoleLabel = (role: UserRole): string => {
  const labels: Record<UserRole, string> = {
    [UserRole.SUPER_ADMIN]: 'Super Admin',
    [UserRole.ADMIN]: 'Admin',
    [UserRole.DEPARTMENT_HEAD]: 'Department Head',
    [UserRole.PROJECT_MANAGER]: 'Project Manager',
    [UserRole.TEAM_LEAD]: 'Team Lead',
    [UserRole.MANAGER]: 'Manager',
    [UserRole.INDIVIDUAL_CONTRIBUTOR]: 'Contributor',
    [UserRole.ENGINEER]: 'Engineer',
  };
  return labels[role] || 'Team Member';
};

const getPerformanceColor = (rating: number) => {
  if (rating >= 4.5) return 'success';
  if (rating >= 3.5) return 'warning';
  return 'error';
};

const getPerformanceLabel = (rating: number): string => {
  if (rating >= 4.5) return 'Excellent';
  if (rating >= 4) return 'Very Good';
  if (rating >= 3) return 'Good';
  if (rating >= 2) return 'Fair';
  return 'Needs Improvement';
};

const getUtilizationColor = (percent: number) => {
  if (percent > 100) return 'error';
  if (percent > 85) return 'warning';
  return 'success';
};

const TeamMemberTrackingCard: React.FC<TeamMemberTrackingCardProps> = ({
  member,
  tasksCount = 0,
  projectsCount = 0,
  completedTasksToday = 0,
  dueTasksCount = 0,
}) => {
  const navigate = useNavigate();

  const handleViewProfile = () => {
    navigate(`/team/${member.id}`);
  };

  const roleColor = getRoleColor(member.role);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: 8,
          transform: 'translateY(-8px)',
        },
        borderTop: `5px solid`,
        borderTopColor: `${roleColor}.main`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background accent */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 120,
          height: 120,
          borderRadius: '50%',
          opacity: 0.1,
          zIndex: 0,
        }}
      />

      {/* Header Section */}
      <CardHeader
        avatar={
          <Badge
            overlap="circular"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: member.isActive ? '#4caf50' : '#f50057',
                color: member.isActive ? '#4caf50' : '#f50057',
                boxShadow: `0 0 0 2px white`,
              },
            }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: `${roleColor}.main`,
                fontSize: '1.8rem',
                fontWeight: 'bold',
                zIndex: 1,
              }}
            >
              {member.avatar}
            </Avatar>
          </Badge>
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {member.name}
            </Typography>
            {projectsCount > 0 && (
              <Chip
                label={`${projectsCount} ${projectsCount === 1 ? 'Project' : 'Projects'}`}
                size="small"
                color="secondary"
                sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.65rem',
                  height: 20,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            )}
          </Box>
        }
        subheader={
          <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center' }}>
            <Chip
              label={getRoleLabel(member.role)}
              size="small"
              color={roleColor}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.7rem' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {member.department}
            </Typography>
          </Stack>
        }
        sx={{ pb: 1.5, zIndex: 1, position: 'relative' }}
      />

      <Divider sx={{ my: 0.5 }} />

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, zIndex: 1, position: 'relative' }}>
        {/* Contact Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Mail size={16} color="#666" />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {member.email}
          </Typography>
        </Box>

        {/* Performance Rating */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem', fontWeight: 600 }}>
              <TrendingUp size={16} />
              Performance
            </Typography>
            <Chip
              label={getPerformanceLabel(member.performanceRating)}
              size="small"
              color={getPerformanceColor(member.performanceRating)}
              variant="filled"
              sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={(member.performanceRating / 5) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor:
                  member.performanceRating >= 4.5
                    ? '#4caf50'
                    : member.performanceRating >= 3.5
                    ? '#ff9800'
                    : '#f44336',
              },
            }}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
            {member.performanceRating.toFixed(1)} / 5.0
          </Typography>
        </Box>

        {/* Utilization */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem', fontWeight: 600 }}>
              <Clock size={16} color="#666" />
              Utilization
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                fontSize: '0.85rem',
                color:
                  member.utilizationPercent > 100
                    ? '#f44336'
                    : member.utilizationPercent > 85
                    ? '#ff9800'
                    : '#4caf50',
              }}
            >
              {formatPercent(member.utilizationPercent)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(member.utilizationPercent, 100)}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: getUtilizationColor(member.utilizationPercent) === 'error' ? '#f44336' : getUtilizationColor(member.utilizationPercent) === 'warning' ? '#ff9800' : '#4caf50',
              },
            }}
          />
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                bgcolor: '#e3f2fd',
                border: '1px solid #bbdefb',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, fontSize: '0.7rem', color: '#1976d2' }}>
                Tasks
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#1976d2' }}>
                {tasksCount}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                bgcolor: '#fff3e0',
                border: '1px solid #ffe0b2',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, fontSize: '0.7rem', color: '#e65100' }}>
                Due Tasks
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#e65100' }}>
                {dueTasksCount}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                bgcolor: '#e8f5e9',
                border: '1px solid #c8e6c9',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, fontSize: '0.7rem', color: '#388e3c' }}>
                Completed
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#388e3c' }}>
                {completedTasksToday}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Skills Tags */}
        {member.skills.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
              Skills
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {member.skills.slice(0, 2).map((skill) => (
                <Chip
                  key={skill}
                  label={skill}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 24 }}
                />
              ))}
              {member.skills.length > 2 && (
                <Chip label={`+${member.skills.length - 2}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 24 }} />
              )}
            </Stack>
          </Box>
        )}
      </CardContent>

      {/* Footer */}
      <Divider />
      <Box sx={{ p: 1.5, display: 'flex', gap: 1, zIndex: 1, position: 'relative' }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleViewProfile}
          endIcon={<ChevronRight size={16} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            py: 0.8,
          }}
        >
          View Profile
        </Button>
      </Box>
    </Card>
  );
};

export default TeamMemberTrackingCard;
